import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ssoApi, TeamSSOConfig } from '../../api/sso';
import { Alert, AlertDescription } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Switch } from '../ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { 
  Shield, 
  Download, 
  TestTube, 
  Trash2, 
  Save, 
  CheckCircle, 
  XCircle,
  ExternalLink,
  Key,
  Users,
  FileText
} from 'lucide-react';

interface SSOConfigPanelProps {
  teamId: string;
  canManage: boolean;
}

export function SSOConfigPanel({ teamId, canManage }: SSOConfigPanelProps) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<TeamSSOConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('config');

  // Form state
  const [formData, setFormData] = useState({
    samlEntityId: '',
    samlSsoUrl: '',
    samlCertificate: '',
    samlNameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
    domains: '',
    forceSso: false,
    autoCreateUser: true,
    autoAssignTeam: true,
  });

  useEffect(() => {
    loadConfig();
  }, [teamId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await ssoApi.getTeamSSOConfig(teamId);
      const configData = (response.data as { config?: TeamSSOConfig })?.config;
      if (configData) {
        setConfig(configData);
        setFormData({
          samlEntityId: configData.samlEntityId || '',
          samlSsoUrl: configData.samlSsoUrl || '',
          samlCertificate: '', // 不显示证书内容
          samlNameIdFormat: configData.samlNameIdFormat || 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
          domains: configData.domains?.join(', ') || '',
          forceSso: configData.forceSso || false,
          autoCreateUser: configData.autoCreateUser !== false,
          autoAssignTeam: configData.autoAssignTeam !== false,
        });
      }
    } catch (err) {
      // 配置不存在是正常的
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const domains = formData.domains
        .split(',')
        .map(d => d.trim())
        .filter(d => d);

      const data = {
        samlEntityId: formData.samlEntityId,
        samlSsoUrl: formData.samlSsoUrl,
        samlCertificate: formData.samlCertificate,
        samlNameIdFormat: formData.samlNameIdFormat,
        domains,
        forceSso: formData.forceSso,
        autoCreateUser: formData.autoCreateUser,
        autoAssignTeam: formData.autoAssignTeam,
      };

      if (config) {
        await ssoApi.updateTeamSSOConfig(teamId, data);
      } else {
        await ssoApi.createTeamSSOConfig({
          teamId,
          ...data,
        });
      }

      setSuccess(t('settings.sso.saveSuccess'));
      loadConfig();
    } catch (err: any) {
      setError(err.response?.data?.message || t('settings.sso.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('settings.sso.deleteConfirm'))) return;

    setLoading(true);
    try {
      await ssoApi.deleteTeamSSOConfig(teamId);
      setConfig(null);
      setFormData({
        samlEntityId: '',
        samlSsoUrl: '',
        samlCertificate: '',
        samlNameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
        domains: '',
        forceSso: false,
        autoCreateUser: true,
        autoAssignTeam: true,
      });
      setSuccess(t('settings.sso.deleteSuccess'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('settings.sso.deleteError'));
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      // 打开测试登录窗口
      const response = await ssoApi.initiateSSOLogin({ teamId, redirect: '/settings' });
      const responseData = response.data as { ssoUrl?: string };
      if (responseData?.ssoUrl) {
        window.open(responseData.ssoUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('settings.sso.testError'));
    } finally {
      setTesting(false);
    }
  };

  const handleDownloadMetadata = async () => {
    try {
      const response = await ssoApi.getSPMetadata(teamId);
      const text = await (response as unknown as Response).text();
      const blob = new Blob([text], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sp-metadata-${teamId}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(t('settings.sso.downloadError'));
    }
  };

  if (!canManage) {
    return (
      <Alert>
        <AlertDescription>{t('settings.sso.noPermission')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{t('settings.sso.title')}</h2>
          <p className="text-muted-foreground">{t('settings.sso.subtitle')}</p>
        </div>
        {config?.status === 'active' && (
          <Badge className="bg-green-500 text-white">
            <CheckCircle className="w-3 h-3 mr-1" />
            {t('settings.sso.active')}
          </Badge>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="config">
            <Shield className="w-4 h-4 mr-2" />
            {t('settings.sso.samlConfig')}
          </TabsTrigger>
          <TabsTrigger value="attributes">
            <Key className="w-4 h-4 mr-2" />
            {t('settings.sso.attributeMapping')}
          </TabsTrigger>
          <TabsTrigger value="security">
            <Users className="w-4 h-4 mr-2" />
            {t('settings.sso.userProvisioning')}
          </TabsTrigger>
          <TabsTrigger value="metadata">
            <FileText className="w-4 h-4 mr-2" />
            {t('settings.sso.spMetadata')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.sso.idpConfig')}</CardTitle>
              <CardDescription>{t('settings.sso.idpConfigDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="samlEntityId">{t('settings.sso.entityId')}</Label>
                <Input
                  id="samlEntityId"
                  value={formData.samlEntityId}
                  onChange={(e) => setFormData({ ...formData, samlEntityId: e.target.value })}
                  placeholder="https://idp.example.com/metadata"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="samlSsoUrl">{t('settings.sso.ssoUrl')}</Label>
                <Input
                  id="samlSsoUrl"
                  value={formData.samlSsoUrl}
                  onChange={(e) => setFormData({ ...formData, samlSsoUrl: e.target.value })}
                  placeholder="https://idp.example.com/sso"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="samlCertificate">{t('settings.sso.certificate')}</Label>
                <textarea
                  id="samlCertificate"
                  value={formData.samlCertificate}
                  onChange={(e) => setFormData({ ...formData, samlCertificate: e.target.value })}
                  placeholder={t('settings.sso.certificatePlaceholder')}
                  className="w-full min-h-[150px] px-3 py-2 border rounded-md text-sm font-mono"
                />
                {config?.samlEntityId && !formData.samlCertificate && (
                  <p className="text-sm text-muted-foreground">{t('settings.sso.certificateKeep')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="domains">{t('settings.sso.domains')}</Label>
                <Input
                  id="domains"
                  value={formData.domains}
                  onChange={(e) => setFormData({ ...formData, domains: e.target.value })}
                  placeholder="example.com, company.org"
                />
                <p className="text-sm text-muted-foreground">{t('settings.sso.domainsHelp')}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attributes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.sso.attributeMapping')}</CardTitle>
              <CardDescription>{t('settings.sso.attributeMappingDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('settings.sso.emailAttribute')}</Label>
                  <Input value="email" disabled />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.sso.nameIdFormat')}</Label>
                  <select
                    value={formData.samlNameIdFormat}
                    onChange={(e) => setFormData({ ...formData, samlNameIdFormat: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">
                      Email Address
                    </option>
                    <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified">
                      Unspecified
                    </option>
                    <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">
                      Persistent
                    </option>
                    <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">
                      Transient
                    </option>
                  </select>
                </div>
              </div>

              <Alert>
                <AlertDescription>{t('settings.sso.attributeAuto')}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.sso.userProvisioning')}</CardTitle>
              <CardDescription>{t('settings.sso.userProvisioningDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('settings.sso.forceSso')}</Label>
                  <p className="text-sm text-muted-foreground">{t('settings.sso.forceSsoDesc')}</p>
                </div>
                <Switch
                  checked={formData.forceSso}
                  onCheckedChange={(checked: boolean) => setFormData({ ...formData, forceSso: checked })}
                />
              </div>

              <div className="h-px bg-zinc-200" />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('settings.sso.autoCreateUser')}</Label>
                  <p className="text-sm text-muted-foreground">{t('settings.sso.autoCreateUserDesc')}</p>
                </div>
                <Switch
                  checked={formData.autoCreateUser}
                  onCheckedChange={(checked: boolean) => setFormData({ ...formData, autoCreateUser: checked })}
                />
              </div>

              <div className="h-px bg-zinc-200" />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t('settings.sso.autoAssignTeam')}</Label>
                  <p className="text-sm text-muted-foreground">{t('settings.sso.autoAssignTeamDesc')}</p>
                </div>
                <Switch
                  checked={formData.autoAssignTeam}
                  onCheckedChange={(checked: boolean) => setFormData({ ...formData, autoAssignTeam: checked })}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metadata" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.sso.spMetadata')}</CardTitle>
              <CardDescription>{t('settings.sso.spMetadataDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {config ? (
                <>
                  <div className="space-y-2">
                    <Label>{t('settings.sso.spEntityId')}</Label>
                    <div className="flex items-center space-x-2">
                      <Input value={config.spEntityId || ''} readOnly />
                      <Button
                        variant="secondary"
                        onClick={() => navigator.clipboard.writeText(config.spEntityId || '')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('settings.sso.acsUrl')}</Label>
                    <div className="flex items-center space-x-2">
                      <Input value={config.spAcsUrl || ''} readOnly />
                      <Button
                        variant="secondary"
                        onClick={() => navigator.clipboard.writeText(config.spAcsUrl || '')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <Button onClick={handleDownloadMetadata} variant="secondary">
                    <Download className="w-4 h-4 mr-2" />
                    {t('settings.sso.downloadMetadata')}
                  </Button>
                </>
              ) : (
                <Alert>
                  <AlertDescription>{t('settings.sso.saveFirst')}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center space-x-2">
          {config && (
            <Button variant="danger" onClick={handleDelete} disabled={loading}>
              <Trash2 className="w-4 h-4 mr-2" />
              {t('settings.sso.disableSSO')}
            </Button>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {config && (
            <Button variant="secondary" onClick={handleTest} disabled={testing}>
              <TestTube className="w-4 h-4 mr-2" />
              {testing ? t('settings.sso.testing') : t('settings.sso.testConnection')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('settings.sso.saving') : t('settings.sso.saveConfig')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SSOConfigPanel;
